/**
 * Class strings for the Tailwind + Headless UI screens.
 *
 * New UI is built with Tailwind, Headless UI (via the catalyst kit) and lucide
 * icons rather than Polaris. These are the few controls every such screen
 * repeats — kept here so the buttons of one form look like the buttons of the
 * next, instead of each file carrying a slightly different copy.
 *
 * Touch sizes: buttons and inputs are at least 44 px high on phones (by width, `max-md:`) and on
 * touch screens of any width, e.g. a tablet (by pointer, `[@media(pointer:coarse)]:`); a mouse keeps
 * the compact size. Inputs use a 16 px font on phones because iOS Safari zooms the page into any
 * focused field smaller than that (the rule for touch screens of every width is in index.css).
 */

export const btnPrimaryCls =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 py-1.5 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

export const btnSecondaryCls =
  "inline-flex items-center justify-center gap-1.5 rounded-lg border border-zinc-300 px-3.5 py-1.5 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800";

/** A row's second, quieter action next to its button ("Sutvarkyta"): plain text, set apart. */
export const btnQuietTextCls =
  "inline-flex min-h-11 items-center px-1 text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline md:min-h-9 [@media(pointer:coarse)]:min-h-11";

export const btnDangerCls =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-600 px-3.5 py-1.5 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 text-sm font-medium text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50";

export const btnSlimCls =
  "inline-flex items-center gap-1 rounded-md border border-zinc-300 px-2.5 py-1 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800";

export const btnSlimPrimaryCls =
  "inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 text-xs font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

export const btnLinkCls =
  "inline-flex items-center gap-1 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 text-sm font-medium text-indigo-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-indigo-400";

export const btnIconCls =
  "inline-flex size-7 max-md:size-11 [@media(pointer:coarse)]:size-11 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-zinc-800 dark:hover:text-zinc-200";

export const inputCls =
  "w-full rounded-lg border border-zinc-300 bg-transparent px-3 py-1.5 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 text-base md:text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-60 aria-[invalid=true]:border-red-500 aria-[invalid=true]:focus:ring-red-500/20 dark:border-zinc-700";

export const labelCls =
  "mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-500";

export const helpTextCls = "mt-1 text-xs text-zinc-500";

export const errorTextCls = "mt-1 text-xs text-red-600 dark:text-red-400";

export const cardCls =
  "rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900";
